import { dataCache } from './DataCache';
import { fetchSessions } from '../api/aiSessionClient';
import { GLOBAL_CHAT_BOOK_ID } from '../pages/ai-chat/AIChatContext';
import type { Book, Volume, Chapter, Setting, Faction, Character, Item, UserPreference, AIPromptTemplate, Conversation } from '../types';

export interface PreloadProgress {
  current: number;
  total: number;
  label: string;
}

const TOTAL_STEPS = 5;

/**
 * 预加载的静默降级开关：任一请求失败即置位。
 * 调用方据此提示"数据可能不完整"，且不再把降级后的空数据写入快照，
 * 避免空快照在后续刷新中长期掩盖真实数据。
 */
let degraded = false;

async function fetchWithRetry<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch {
    degraded = true;
    return fallback;
  }
}

/**
 * 预加载全部数据。返回 true 表示发生过降级（部分数据缺失）。
 */
export async function preloadAllData(
  onProgress: (p: PreloadProgress) => void,
): Promise<boolean> {
  degraded = false;
  let step = 0;
  const advance = (label: string) => {
    step++;
    onProgress({ current: step, total: TOTAL_STEPS, label });
  };

  advance('加载作品列表...');
  const [books, deletedBooks] = await Promise.all([
    fetchWithRetry(() => window.api.getBooks(), [] as Book[]),
    fetchWithRetry(() => window.api.getDeletedBooks(), [] as Book[]),
  ]);
  dataCache.books = books;
  dataCache.deletedBooks = deletedBooks;

  if (books.length === 0) {
    advance('加载系统设置...');
    const [templates, prefs] = await Promise.all([
      fetchWithRetry(() => window.api.getAIPromptTemplates(), [] as AIPromptTemplate[]),
      fetchWithRetry(() => window.api.getAllUserPreferences(), [] as UserPreference[]),
    ]);
    dataCache.aiPromptTemplates = templates;
    dataCache.userPreferences = prefs;
    advance('加载AI对话...');
    const sessions = await fetchWithRetry(() => fetchSessions(GLOBAL_CHAT_BOOK_ID, 'normal', 'meta') as Promise<Conversation[]>, [] as Conversation[]);
    dataCache.setAISessions(GLOBAL_CHAT_BOOK_ID, sessions);
    advance('同步完成');
    return degraded;
  }

  advance('加载系统设置...');
  const [templates, prefs] = await Promise.all([
    fetchWithRetry(() => window.api.getAIPromptTemplates(), [] as AIPromptTemplate[]),
    fetchWithRetry(() => window.api.getAllUserPreferences(), [] as UserPreference[]),
  ]);
  dataCache.aiPromptTemplates = templates;
  dataCache.userPreferences = prefs;

  advance('加载作品数据...');
  const allSessions: Promise<Conversation[]>[] = [];

  for (const book of books) {
    const bookId = book.id;

    allSessions.push(
      fetchWithRetry(() => fetchSessions(bookId, 'normal', 'meta') as Promise<Conversation[]>, [] as Conversation[]),
    );

    const [volumes, worldData] = await Promise.all([
      fetchWithRetry(() => window.api.getVolumes(bookId), [] as Volume[]),
      fetchWithRetry(() => window.api.getWorldData(bookId), { settings: [] as Setting[], factions: [] as Faction[], characters: [] as Character[], items: [] as Item[], outline: '' as string }),
    ]);

    dataCache.setVolumes(bookId, volumes);
    dataCache.setSettings(bookId, worldData.settings);
    dataCache.setOutline(bookId, worldData.outline);
    dataCache.setFactions(bookId, worldData.factions);
    dataCache.setCharacters(bookId, worldData.characters);
    dataCache.setItems(bookId, worldData.items);

    const chaptersByVolume = new Map<string, Chapter[]>();
    const chapResults = await Promise.all(
      volumes.map(async (vol) => {
        const chaps = await fetchWithRetry(
          () => window.api.getChapters(vol.id),
          [] as Chapter[],
        );
        return { volId: vol.id, chaps };
      }),
    );
    for (const { volId, chaps } of chapResults) {
      chaptersByVolume.set(volId, chaps);
    }
    dataCache.setChaptersByVolume(bookId, chaptersByVolume);

    const lastChapterPref = prefs.find(p => p.preference_name === `lastChapter_${bookId}`);
    if (lastChapterPref?.preference_value) {
      const chapterId = lastChapterPref.preference_value;
      for (const chaps of chaptersByVolume.values()) {
        const found = chaps.find(c => c.id === chapterId);
        if (found) {
          const content = await fetchWithRetry(
            () => window.api.getChapterContent(chapterId),
            '',
          );
          const contentsMap = dataCache.getChapterContentsRaw(bookId) ?? new Map();
          contentsMap.set(chapterId, content);
          dataCache.setChapterContents(bookId, contentsMap);
          break;
        }
      }
    }
  }

  advance('加载AI对话...');
  const sessionsResults = await Promise.all(allSessions);
  books.forEach((book, i) => {
    dataCache.setAISessions(book.id, sessionsResults[i]);
  });
  const sessions = await fetchWithRetry(() => fetchSessions(GLOBAL_CHAT_BOOK_ID, 'normal', 'meta') as Promise<Conversation[]>, [] as Conversation[]);
  dataCache.setAISessions(GLOBAL_CHAT_BOOK_ID, sessions);

  advance('同步完成');
  return degraded;
}
